import { Link } from 'react-router-dom'
import { useMaintenanceSchedules, computeDueStatus } from '../../hooks/useMaintenance'
import { usePrinters } from '../../hooks/usePrinters'

export default function MaintenanceDue() {
  const { data: schedules } = useMaintenanceSchedules()
  const { data: printers } = usePrinters()

  if (!schedules || !printers) return <div>Loading…</div>

  const printerById = new Map(printers.map((p) => [p.id, p]))
  const activeSchedules = schedules.filter((s) => s.is_active)

  const items = activeSchedules.map((s) => {
    const printer = printerById.get(s.printer_id)
    const hours = printer ? Number(printer.total_print_hours) : 0
    return { schedule: s, printer, due: computeDueStatus(s, hours) }
  })

  const overdue = items.filter((i) => i.due.isOverdue)
  const dueSoon = items.filter((i) => !i.due.isOverdue && i.due.isDue)
  const upcoming = items.filter((i) => !i.due.isDue).sort((a, b) => a.due.remaining - b.due.remaining)

  return (
    <div>
      {overdue.length === 0 && dueSoon.length === 0 && (
        <div style={{
          padding: 'var(--space-6)',
          textAlign: 'center',
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-lg)',
          color: 'var(--color-text-muted)',
          marginBottom: 'var(--space-4)',
        }}>
          🎉 Everything&apos;s up to date.
        </div>
      )}

      {overdue.length > 0 && (
        <Section title="⚠ Overdue" color="var(--color-danger)" items={overdue} />
      )}
      {dueSoon.length > 0 && (
        <Section title="Due now" color="var(--color-warning)" items={dueSoon} />
      )}
      {upcoming.length > 0 && (
        <Section title="📅 Coming up" color="var(--color-text-muted)" items={upcoming.slice(0, 10)} />
      )}
    </div>
  )
}

function Section({ title, color, items }: {
  title: string
  color: string
  items: Array<{ schedule: { id: string; printer_id: string; task_type: string }; printer?: { name: string }; due: { label: string } }>
}) {
  return (
    <div style={{ marginBottom: 'var(--space-6)' }}>
      <h3 style={{
        fontSize: 'var(--font-size-sm)',
        color,
        marginBottom: 'var(--space-3)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        fontWeight: 700,
      }}>
        {title}
      </h3>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {items.map((item) => (
          <li key={item.schedule.id} style={{
            background: 'var(--color-surface)',
            padding: 'var(--space-3)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--space-2)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                {item.printer?.name ?? '?'} — {formatTaskType(item.schedule.task_type)}
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>
                {item.due.label}
              </div>
            </div>
            <Link
              to={`/maintenance/log?schedule=${item.schedule.id}`}
              style={{
                padding: 'var(--space-2) var(--space-3)',
                background: 'var(--color-primary)',
                color: 'white',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Log it
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

function formatTaskType(t: string): string {
  return t.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}
